import React, { useCallback, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Product } from '@Types/product/Product';
import { Swiper, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import Price from 'components/commercetools-ui/price';
import { FullPageWidthWrapper } from 'components/tailgrids-ui/full-bleed';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'helpers/reference';
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import { useCart } from 'frontastic';
import { SwiperComponent } from 'swiper/angular';
import { CustomImage } from 'lib/customImage';

export interface Props {
  products: Product[];
  title: string;
  subline?: string;
  ctaLabel?: string;
  ctaLink?: Reference;
  loading?: boolean;
  inCheckout?: boolean;
}

export default function ProductCarousel({ products, title, loading, inCheckout }: Props) {
  const [swiper, setSwiper] = useState<any>();
  const productsLoading = loading && products && products.length === 0;
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const handlePrev = useCallback(() => {
    if (!swiper) return;
    swiper?.slidePrev();
  }, [swiper]);

  const handleNext = useCallback(() => {
    if (!swiper) return;
    swiper?.slideNext();
  }, [swiper]);

  return (
    <FullPageWidthWrapper>
      <div className="container mx-auto max-w-[1280px] overflow-hidden px-4 pb-[120px] pt-20 font-urbanist sm:px-10 lg:px-4">
        <div className="mb-12 max-w-[510px] text-left">
          <span className="mb-2 block text-lg font-semibold text-primary">{title}</span>
          <h2 className="text-dark text-3xl font-bold sm:text-4xl md:text-[40px]">
            {formatProductMessage({ id: 'interesting.products', defaultMessage: 'You may also like' })}
          </h2>
        </div>

        <Swiper
          onSwiper={(swiper) => setSwiper(swiper)}
          breakpoints={{
            370: {
              width: 370,
              slidesPerView: 1,
            },

            600: {
              width: 600,
              slidesPerView: 2,
            },
            768: {
              width: 768,
              slidesPerView: 1.8,
            },
            1024: {
              width: 1024,
              slidesPerView: 3,
            },
            1280: {
              width: 1280,
              slidesPerView: 4,
            },
          }}
          loop={products.length >= 3}
          spaceBetween={10}
          //ref={sliderRef}
          itemRef={'ss'}
          className="!overflow-visible"
        >
          {products.map((product) => (
            <SwiperSlide key={product.productId} className="flex">
              <ProductCard product={product} inCheckout={inCheckout} />
            </SwiperSlide>
          ))}

          <div role="status" className="absolute inset-x-0 bottom-2 flex items-center justify-center">
            <div className={productsLoading ? '' : 'hidden'}>
              <svg
                aria-hidden="true"
                className="mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
            <div className={!loading && products && products.length === 0 ? '' : 'hidden'}>
              {formatProductMessage({ id: 'noProductsFound', defaultMessage: 'No products found.' })}
            </div>
          </div>
          {products.length > 1 && (
            <div className="absolute inset-x-0 -bottom-[68px] z-50 flex items-center justify-center space-x-3">
              <div className="prev-arrow cursor-pointer" onClick={handlePrev}>
                <button className="border-body-color text-body-color flex h-10 w-10 items-center justify-center rounded-full border bg-white hover:border-primary hover:bg-primary hover:text-white">
                  <svg width="15" height="13" viewBox="0 0 15 13" className="fill-current">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.24264 11.8033L0.46967 7.03037C0.176777 6.73748 0.176777 6.2626 0.46967 5.96971L5.24264 1.19674C5.53553 0.903845 6.01041 0.903845 6.3033 1.19674C6.59619 1.48963 6.59619 1.96451 6.3033 2.2574L2.81066 5.75004H14C14.4142 5.75004 14.75 6.08583 14.75 6.50004C14.75 6.91425 14.4142 7.25004 14 7.25004H2.81066L6.3033 10.7427C6.59619 11.0356 6.59619 11.5104 6.3033 11.8033C6.01041 12.0962 5.53553 12.0962 5.24264 11.8033Z"
                    ></path>
                  </svg>
                </button>
              </div>
              <div className="next-arrow cursor-pointer" onClick={handleNext}>
                <button className="border-body-color text-body-color flex h-10 w-10 items-center justify-center rounded-full border bg-white hover:border-primary hover:bg-primary hover:text-white">
                  <svg width="15" height="13" viewBox="0 0 15 13" className="fill-current">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.75736 11.8033L14.5303 7.03037C14.8232 6.73748 14.8232 6.2626 14.5303 5.96971L9.75736 1.19674C9.46447 0.903845 8.98959 0.903845 8.6967 1.19674C8.40381 1.48963 8.40381 1.96451 8.6967 2.2574L12.1893 5.75004H1C0.585786 5.75004 0.25 6.08583 0.25 6.50004C0.25 6.91425 0.585786 7.25004 1 7.25004H12.1893L8.6967 10.7427C8.40381 11.0356 8.40381 11.5104 8.6967 11.8033C8.98959 12.0962 9.46447 12.0962 9.75736 11.8033Z"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </Swiper>
      </div>
    </FullPageWidthWrapper>
  );
}

const ProductCard = ({ product, inCheckout }: { product: Product; inCheckout: boolean }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { addItem } = useCart();

  const isNewProduct = useMemo(() => {
    return !!product?.categories?.find(
      (category) => category.slug.includes('new') || category.slug.includes('neu') || category.slug.includes('nouveau'),
    );
  }, []);

  const isOnSale = useMemo(() => {
    return !!product?.categories?.find((category) => category.slug.includes('sale') || category.slug.includes('vente'));
  }, []);

  return (
    <div className="flex flex-col justify-between gap-2">
      <Link href={product?._url ?? ''}>
        <div className="w-[270px] cursor-pointer bg-white" data-testid={product?.productId}>
          <div className="relative mb-5 w-full overflow-hidden rounded-md">
            {/* <img src={product.variants[0].images[0]} alt="product" className="h-[300px] w-full object-contain" /> */}
            <div className="rounded-[4px] border border-neutral-200">
              <CustomImage imgSrc={product.variants[0].images[0]} width={270} height={300} />
            </div>
            <div className="absolute right-2 top-2 flex flex-col items-end justify-between gap-2">
              {isNewProduct && (
                <div className="flex items-center justify-center gap-1 rounded bg-secondary px-2 py-1 text-white">
                  <p className="text-xs font-normal">{formatProductMessage({ id: 'new', defaultMessage: 'New' })}</p>
                  <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.1937 4.98125L7.79997 4.45625L6.26247 1.23125C6.14997 1.00625 5.84997 1.00625 5.73747 1.23125L4.19997 4.475L0.82497 4.98125C0.58122 5.01875 0.48747 5.3375 0.67497 5.50625L3.13122 8.0375L2.54997 11.5812C2.51247 11.825 2.75622 12.0312 2.98122 11.8813L6.03747 10.2125L9.07497 11.8813C9.28122 11.9938 9.54372 11.8062 9.48747 11.5812L8.90622 8.0375L11.3625 5.50625C11.5125 5.3375 11.4375 5.01875 11.1937 4.98125Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}

              {isOnSale && (
                <div className="flex items-center justify-center gap-1 rounded bg-[#E77474] px-2 py-1 text-white">
                  <p className="text-xs font-normal">{formatProductMessage({ id: 'sale', defaultMessage: 'Sale' })}</p>
                  <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_211_184)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.2601 1.20969C5.42417 1.0923 5.641 1.07931 5.81792 1.17627C6.36504 1.47613 7.11161 2.01577 7.66876 2.89761C8.23232 3.78957 8.58064 4.9988 8.37038 6.58826C8.36791 6.60694 8.36706 6.62081 8.367 6.63083C8.39186 6.63517 8.4391 6.63758 8.51054 6.6164C8.71512 6.55576 9.01409 6.31551 8.9819 5.74971C8.96899 5.52283 9.10352 5.3134 9.31522 5.2308C9.52692 5.1482 9.76773 5.21117 9.91189 5.38684C10.6705 6.31124 11.5449 7.85631 11.6287 9.4849C11.6712 10.3114 11.5097 11.168 11.0168 11.9625C10.5246 12.7559 9.72553 13.4494 8.55615 13.9915C8.34078 14.0913 8.08522 14.0342 7.93283 13.8522C7.78043 13.6702 7.76912 13.4086 7.90524 13.2141C8.1604 12.8496 8.49801 11.882 7.78262 10.9877C7.77356 10.9764 7.76498 10.9647 7.75689 10.9527C7.72895 10.9111 7.70497 10.8827 7.66755 10.8383C7.64267 10.8088 7.61184 10.7722 7.56996 10.7201C7.4816 10.61 7.37428 10.4625 7.29508 10.2655C7.24412 10.1387 7.20934 10.0035 7.19191 9.85713C6.91137 10.0857 6.63847 10.368 6.42307 10.6986C5.9921 11.3601 5.78034 12.2249 6.22345 13.3179C6.30382 13.5162 6.25587 13.7433 6.10224 13.8922C5.94861 14.041 5.72003 14.0818 5.52441 13.9952C4.57229 13.5738 3.33288 12.6562 2.75581 11.2397C2.16059 9.77869 2.32039 7.91545 3.94633 5.74752L3.94633 5.74752C4.52866 4.9711 5.35445 3.48153 5.0484 1.72687C5.01373 1.52812 5.09602 1.32709 5.2601 1.20969ZM8.36938 6.65336C8.36922 6.65356 8.3686 6.65219 8.36804 6.6487C8.36927 6.65142 8.36955 6.65317 8.36938 6.65336ZM6.1605 2.71032C6.09608 4.35064 5.31814 5.66846 4.78633 6.37753C3.32844 8.32138 3.30264 9.79896 3.72821 10.8436C3.99146 11.4897 4.44619 12.0175 4.93744 12.4129C4.87354 11.5264 5.13335 10.7547 5.54332 10.1254C6.126 9.23111 7.00047 8.63589 7.63632 8.3527C7.82495 8.26869 8.04531 8.30305 8.1994 8.4405C8.35349 8.57796 8.41269 8.79297 8.35069 8.98993C8.17683 9.54219 8.22822 9.77161 8.26929 9.87379C8.29316 9.93314 8.32874 9.98799 8.38866 10.0626C8.39893 10.0754 8.41339 10.0927 8.4305 10.1131C8.48172 10.1743 8.55678 10.264 8.61494 10.3474C9.15478 11.0302 9.2986 11.7402 9.25662 12.3549C9.64189 12.0533 9.92326 11.7334 10.1246 11.409C10.4869 10.8249 10.6135 10.1881 10.5801 9.53883C10.5329 8.62096 10.1655 7.69679 9.71826 6.94073C9.48965 7.28539 9.16023 7.51898 8.80895 7.62311C8.48506 7.71911 8.09695 7.71397 7.77832 7.50889C7.42502 7.28151 7.27217 6.88361 7.32945 6.45057C7.50844 5.09747 7.20954 4.13658 6.78109 3.45844C6.5948 3.16359 6.38 2.91605 6.1605 2.71032Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_211_184">
                        <rect width="14" height="14" fill="white" transform="translate(0 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              )}
            </div>
          </div>
          <div className="text-left">
            <h3 className="xs:text-xl text-lg font-semibold text-black hover:text-primary">
              {product.name.slice(0, 27)}...
            </h3>
            <div className="flex items-center">
              {product.variants[0].discountedPrice ? (
                <div className="flex items-center">
                  <Price price={product.variants[0].discountedPrice} className="text-lg font-semibold text-gray-900" />
                  <Price
                    price={product.variants[0]?.price}
                    className="pl-6 text-sm font-medium text-[#808080] line-through decoration-[#808080] decoration-1"
                  />
                </div>
              ) : (
                <Price price={product.variants[0]?.price} className=" text-lg font-semibold text-gray-900" />
              )}
            </div>
          </div>
        </div>
      </Link>
      {inCheckout && (
        <button
          className="mt-2 flex w-full justify-center rounded border border-transparent bg-primary py-2 px-4 text-sm font-medium text-white transition-colors duration-200 ease-out hover:bg-[#33B8A1] disabled:bg-[#EDEDED]"
          onClick={async () =>
            await addItem(
              product.variants.find((variant) => variant.isOnStock),
              1,
            )
          }
          disabled={!product.variants.find((variant) => variant.isOnStock)}
        >
          {formatProductMessage({ id: 'addToCart', defaultMessage: 'Add to cart' })}
        </button>
      )}
    </div>
  );
};
